/**
 * Акция (скидка со сроком) карточек Популярных и стилей/трендов.
 *
 * Зеркало money-path бэка (source_price_override → effective_source_price):
 * пока срок не истёк, списание = discount_price — С УРОВНЕЙ-СКИДОК промо
 * может быть НИЖЕ базовой цены модели (бэк применяет её по-настоящему, гейты
 * себестоимости/реф-пола пройдены при назначении в админке); после срока —
 * снова обычная цена (floor max(база, price_override)). Фронт показывает ту
 * же эффективную цену в `priceOverride` карточки + процент/остаток срока для
 * плашки «−N%». Авторитетна всегда цена бэка — здесь только отображение.
 */

export interface ActiveDiscount {
	/** Скидочная цена (кредиты) — эффективный priceOverride на время акции. */
	price: number
	/** Размер скидки от обычной цены, % (округлённый, всегда > 0). */
	percent: number
	/** Сколько осталось: «7 дн.» / «3 ч.» / «<1 ч.». */
	leftLabel: string
	/** Конец акции: «22.07, 18:00» (локальное время юзера) — для тултипов. */
	untilLabel: string
	/** Конец акции сырой ISO-строкой — едет в пин (discountUntil): виджет
	 *  сам проверяет живость промо на момент расчёта цены. */
	until: string
}

/**
 * Промо-цена этапа тренда: ceil(credits × (1 − pct/100)), минимум 1.
 * Формула = admin popularCardPipelines.pipelineStagePromo (ей же админ пишет
 * discount_price в шаги шаблона, по ней же бэк списывает) — менять синхронно.
 */
export function trendStagePromo(credits?: number | null, pct?: number | null): number | undefined {
	if (!credits || !pct || pct <= 0 || pct >= 100) return undefined
	return Math.max(1, Math.ceil(credits * (1 - pct / 100)))
}

/** Ценовая часть pipeline-конфига тренд-карточки (подмножество админского). */
export interface TrendPipePricing {
	enabled?: boolean
	photo?: { credits?: number; discount_price?: number }
	video?: { credits?: number; discount_price?: number }
	discount_percent?: number
	discount_until?: string
}

/**
 * Промо-цена этапа: заданная в токенах (discount_price — новое) → из легаси-
 * процента → обычная цена. Зеркало admin popularCardPipelines.promo — синхронно.
 */
function stagePromoPrice(s?: { credits?: number; discount_price?: number }, pct?: number): number {
	if (s?.discount_price && s.credits && s.discount_price > 0 && s.discount_price < s.credits)
		return s.discount_price
	return trendStagePromo(s?.credits, pct) ?? s?.credits ?? 0
}

/** Акция тренда: скидка на сумму цен обоих этапов (плашка −N% витрины). */
export function trendDiscount(pipe?: TrendPipePricing | null): ActiveDiscount | null {
	if (!pipe?.enabled) return null
	const base = (pipe.photo?.credits || 0) + (pipe.video?.credits || 0)
	const promo = stagePromoPrice(pipe.photo, pipe.discount_percent) + stagePromoPrice(pipe.video, pipe.discount_percent)
	return activeDiscount(base, promo, pipe.discount_until)
}

/** Витринная цена тренда «под ключ»: сумма этапов; при живой акции — промо. */
export function trendTotalCost(pipe?: TrendPipePricing | null): number {
	if (!pipe?.enabled) return 0
	const base = (pipe.photo?.credits || 0) + (pipe.video?.credits || 0)
	return trendDiscount(pipe)?.price ?? base
}

/** Активная скидка: срок в будущем и цена строго ниже обычной, иначе null. */
export function activeDiscount(
	regular?: number | null,
	discount?: number | null,
	until?: string | null,
): ActiveDiscount | null {
	if (!regular || !discount || !until || discount <= 0 || discount >= regular) return null
	const end = new Date(until).getTime()
	if (!Number.isFinite(end) || end <= Date.now()) return null
	const msLeft = end - Date.now()
	const days = Math.floor(msLeft / 86_400_000)
	const hours = Math.floor(msLeft / 3_600_000)
	const leftLabel = days >= 1 ? `${days} дн.` : hours >= 1 ? `${hours} ч.` : '<1 ч.'
	const percent = Math.round((1 - discount / regular) * 100)
	const untilLabel = new Date(end).toLocaleString('ru-RU', {
		day: '2-digit',
		month: '2-digit',
		hour: '2-digit',
		minute: '2-digit',
	})
	return { price: discount, percent, leftLabel, untilLabel, until }
}

/**
 * Гейт промо «настройки не дороже пресета» для витрин БЕЗ ценника (ленты
 * трендов, видео-витрина): плашка «−N%» не должна обещать скидку, которой
 * не будет. Зеркало бэка (card_discount.effective_source_price) и плиток
 * «Популярных»: расчёт по параметрам карточки выше её обычной цены → бэк
 * спишет расчёт, промо не применяется, плашку не показываем.
 *
 * Возвращает карточку как есть либо копию без discount_price. Тренды
 * (pipeline) не трогаем — их этапы задаёт шаблон, юзер параметры не меняет.
 */
export function gatePromoByCost<
	T extends { price_override?: number; discount_price?: number; pipeline?: TrendPipePricing },
>(card: T, modelCost: number): T {
	if (card.pipeline?.enabled || !card.discount_price) return card
	const base = card.price_override ?? modelCost
	if (modelCost <= base) return card
	return { ...card, discount_price: undefined }
}
